import type { AudioRecorderContainerRef } from "@features/session/containers/AudioRecorderContainer";
import useItemAnsweredState from "@features/session/hooks/useItemAnsweredState";
import { isWeb } from "@memorang/helpers/platform";
import { getSubmittedRawInputValue } from "@memorang/helpers/user-submitted-answer";
import type {
	BaseItemSchema,
	ItemResponse,
	OriginalItemTypes,
} from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { useToast } from "@memorang/ui/components/Toast";
import { useDevice } from "@memorang/ui/useDevice";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import ItemWrapper from "../../ItemWrapper";
import Speaking from "./variants/Speaking";

type Props = {
	item: BaseItemSchema;
	response: Extract<ItemResponse, { type: "recording" }>;
	toggleShowReadMore?: () => void;
	originalItemType: OriginalItemTypes;
};

const SpeakingContainer = ({
	item,
	response,
	toggleShowReadMore,
	originalItemType,
}: Props) => {
	const { isMobile } = useDevice();
	const { showToast } = useToast();
	const { t } = useTranslation();

	const { isAnswered } = useItemAnsweredState(item);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);

	const storedResponse = userResponse?.[item.id!];
	const currentUserResponse =
		(typeof storedResponse === "string" ? storedResponse : undefined) ??
		(item.answer?.aiResponse?.answer || undefined) ??
		getSubmittedRawInputValue(item.answer, "audio");

	const audioRecorderContainerRef = useRef<AudioRecorderContainerRef>(null);

	return (
		<ItemWrapper
			item={item}
			response={response}
			instructions={
				typeof item.content?.instruction === "string"
					? item.content.instruction
					: item.content?.instruction?.text
			}
			toggleShowReadMore={() => {
				if (audioRecorderContainerRef.current?.isRecording) {
					audioRecorderContainerRef.current?.stop();
					showToast({
						message: t("recorder.uiElementPressed"),
					});
				}
				toggleShowReadMore?.();
			}}
			originalItemType={originalItemType}
			footer={
				!isWeb && (
					<Box padding={16} paddingBottom={24}>
						<Speaking
							itemId={item.id!}
							currentQuestionAnswered={isAnswered}
							currentUserResponse={currentUserResponse}
							maximumDuration={response.maxDuration}
							audioRecorderContainerRef={audioRecorderContainerRef}
						/>
					</Box>
				)
			}
		>
			{isWeb && (
				<Box paddingHorizontal={isMobile ? 16 : 0} paddingBottom={24}>
					<Speaking
						itemId={item.id!}
						currentQuestionAnswered={isAnswered}
						currentUserResponse={currentUserResponse}
						maximumDuration={response.maxDuration}
						audioRecorderContainerRef={audioRecorderContainerRef}
					/>
				</Box>
			)}
		</ItemWrapper>
	);
};

export default SpeakingContainer;
